import { formatNumber } from '@angular/common';

import { BcmService } from '@modules/bcm/bcm.service';
import { RoundNumberFactor, roundNumber } from '@modules/bcm/@shared/pipes/dynamic-price-rounded.pipe';
import { StaticGlobals } from '@modules/bcm/@shared/static-globals';

// Rounding factor for money display — Swiss 5-Rappen when the tenant enabled
// `roundPricesTo5SwissCentimes`, otherwise commercial 2-decimal rounding.
// Mirrors getPriceDataFromInvoicePositions() so the dialog preview matches the
// real invoice.
function roundFactor(): RoundNumberFactor {
    return StaticGlobals.roundTo5Cent
        ? RoundNumberFactor.Swiss5CentimesRounding
        : RoundNumberFactor.TwoDecimals;
}

// Shared gross-price formula for booking-v2. Used by the services tab (live
// line editing) and the summary step (read-only totals) so both compute the
// gross identically. Mirrors getPriceDataFromInvoicePositions() branch-for-branch
// so per-line grosses match the invoice: the tenant-wide `netPrices` flag decides
// whether the stored `price` is net (add VAT) or already gross (as-is). Rounds
// net-first then the derived gross (commercial or Swiss 5-Rappen), same order as
// the invoice calc.
export function grossOf(quantity: number, price: number, vat: number, discount?: number): number {
    const factor = roundFactor();
    const line = quantity * price * (1 - (discount ?? 0) / 100);
    // Brutto-Eingabe: the stored price is already gross → no VAT on top (would
    // double-tax). Matches the invoice's `!netPrices` branch.
    if (!StaticGlobals.netPrices) {
        return roundNumber(line, factor);
    }
    const netLine = roundNumber(line, factor);
    return roundNumber(netLine * (1 + vat / 100), factor);
}

// Currency-less money format following the tenant locale (defaults to de-DE).
// Applies the same commercial/Swiss rounding before formatting so raw floats
// (e.g. 2.975 → 2,98) never show Angular's truncated `formatNumber` result.
export function formatMoney(value: number): string {
    const v = roundNumber(value ?? 0, roundFactor());
    try {
        return formatNumber(v, BcmService.currentLocale, '1.2-2');
    } catch {
        return formatNumber(v, 'de-DE', '1.2-2');
    }
}

// Quantity display following the tenant locale (defaults to de-DE). Unlike money,
// quantities carry no forced decimals: 1 stays "1", 18,75 keeps both places, and the
// grouping separator only kicks in from 1.000 upwards. Up to 4 decimals, matching the
// legacy position/subscription tables (`| number:'1.0-4'`) so the wizard preview and the
// resulting invoice show the same quantity. (#6949)
export function formatQuantity(value: number): string {
    const v = value ?? 0;
    try {
        return formatNumber(v, BcmService.currentLocale, '1.0-4');
    } catch {
        return formatNumber(v, 'de-DE', '1.0-4');
    }
}
